// Coluna do kanban de deals (Story 11.5 / AC2).
// Droppable (@dnd-kit/core) + SortableContext pros cards. Header com nome do
// estágio + contagem + soma de valor. Empty state por coluna (AC9).
// Estágios terminais (ganho/perdido) são SÓ destino de drop — header destacado
// com a cor de estado correspondente, sem cards persistidos (ver AUTO-DECISION).

import type { ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { cn } from '@control-center/ui';

import { STAGE_LABELS, sumDealMoney } from '../../../lib/deal-helpers';
import type { CrmDealStageSchema } from '@control-center/shared';
import type { CrmDealEntity } from '../../../lib/crm-deals-api';

type ColumnTone = 'neutral' | 'good' | 'bad';

export interface KanbanColumnProps {
  stage: CrmDealStageSchema;
  deals: CrmDealEntity[];
  /** Coluna terminal (drop-only) — não lista cards, só recebe o drop. */
  terminal?: boolean;
  /** Tom da coluna terminal (good=ganho, bad=perdido). */
  tone?: ColumnTone;
  children?: ReactNode;
}

const TONE_HEADER: Record<ColumnTone, string> = {
  neutral: 'text-ink',
  good: 'text-good',
  bad: 'text-bad',
};

export function KanbanColumn({
  stage,
  deals,
  terminal,
  tone = 'neutral',
  children,
}: KanbanColumnProps): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: stage, data: { stage } });

  const count = deals.length;
  const total = sumDealMoney(deals.map((d) => d.value));

  return (
    <section
      ref={setNodeRef}
      data-testid={`kanban-column-${stage}`}
      aria-label={STAGE_LABELS[stage]}
      className={cn(
        'flex w-[280px] shrink-0 flex-col rounded-[14px] border bg-panel transition-colors',
        isOver ? 'border-line-2 bg-panel-2' : 'border-line',
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-[10px]">
        <div className="flex items-center gap-2">
          <h2 className={cn('text-[13px] font-semibold tracking-[-0.005em]', TONE_HEADER[tone])}>
            {STAGE_LABELS[stage]}
          </h2>
          {!terminal && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-panel-2 px-1.5 text-[11px] font-medium tabular-nums text-muted-2">
              {count}
            </span>
          )}
        </div>
        {!terminal && <span className="num text-[11px] text-muted-2">{total}</span>}
      </header>

      <div className="flex min-h-[120px] flex-1 flex-col gap-2 p-2">
        {terminal ? (
          <DropHint tone={tone} label={STAGE_LABELS[stage]} />
        ) : (
          <SortableContext items={deals.map((d) => d.id)} strategy={verticalListSortingStrategy}>
            {count === 0 ? <EmptyColumn /> : children}
          </SortableContext>
        )}
      </div>
    </section>
  );
}

function EmptyColumn(): JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-center rounded-[10px] border border-dashed border-line px-3 py-6 text-center text-[11px] text-muted-3">
      Nenhum deal aqui.
    </div>
  );
}

function DropHint({ tone, label }: { tone: ColumnTone; label: string }): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-1 items-center justify-center rounded-[10px] border border-dashed px-3 py-6 text-center text-[11px]',
        tone === 'good' ? 'border-good/40 text-good' : 'border-bad/40 text-bad',
      )}
    >
      Solte aqui pra marcar como {label.replace('Fechamento ', '').toLowerCase()}.
    </div>
  );
}
